import _ from 'lodash';

import { ActionStepStatus, ActionStepType } from 'constants/aiChat';

import { ListItemStatus, MessageContentType } from './BotMessage.constants';
import { formatActionStepContentCopyText } from './components/MessageContent/ActionStepContent/ActionStepContent.utils';
import { formatConfirmActionContentCopyText } from './components/MessageContent/ConfirmActionContent';
import { formatGuidelineExpanderCardContentCopyText } from './components/MessageContent/GuidelineExpanderCardContent/GuidelineExpanderCardContent.utils';
import { formatListContentCopyText } from './components/MessageContent/ListContent';
import { formatTextContentCopyText } from './components/MessageContent/TextContent';

const isNonEmptyString = value => _.isString(value) && !!value.length;

const listItemStatusValues = Object.values(ListItemStatus);

export const isTextContentProps = props =>
  !_.isNil(props) && _.isObject(props) && isNonEmptyString(props.text);

export const isConfirmActionContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  isNonEmptyString(props.submitLabel) &&
  isNonEmptyString(props.cancelLabel) &&
  _.isFunction(props.onSubmit) &&
  _.isFunction(props.onCancel);

const isValidListItem = item =>
  !_.isNil(item) &&
  _.isObject(item) &&
  isNonEmptyString(item.label) &&
  isNonEmptyString(item.text) &&
  listItemStatusValues.includes(item.status);

export const isListContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  Array.isArray(props.listProps) &&
  !!props.listProps.length &&
  props.listProps.every(isValidListItem);

const actionStepBlockStatusValues = Object.values(ActionStepStatus);

export const isActionStepContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  isNonEmptyString(props.label) &&
  actionStepBlockStatusValues.includes(props.status);

export const isActionStepGroupContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  isNonEmptyString(props.groupId) &&
  isNonEmptyString(props.label) &&
  actionStepBlockStatusValues.includes(props.status);

export const isGuidelineExpanderCardContentProps = props =>
  !_.isNil(props) && _.isObject(props) && isNonEmptyString(props.citationId);

export const contentTypeCopyFormatterMap = {
  [MessageContentType.TEXT]: contentProps => formatTextContentCopyText(contentProps.text),
  [MessageContentType.CONFIRM_ACTION]: contentProps =>
    formatConfirmActionContentCopyText(contentProps.submitLabel, contentProps.cancelLabel),
  [MessageContentType.LIST]: contentProps => formatListContentCopyText(contentProps.listProps),
  [MessageContentType.ACTION_STEP]: contentProps => formatActionStepContentCopyText(contentProps),
  [MessageContentType.ACTION_STEP_GROUP]: contentProps => contentProps.label,
  [MessageContentType.GUIDELINE_EXPANDER_CARD]: contentProps =>
    formatGuidelineExpanderCardContentCopyText(contentProps),
};

export const groupActionStepBlocks = items => {
  const stepsByParentGroupId = new Map();

  items.forEach(item => {
    if (item.contentType !== MessageContentType.ACTION_STEP) {
      return;
    }

    const parentGroupId = item.contentProps?.parentGroupId;

    if (!isNonEmptyString(parentGroupId)) {
      return;
    }

    const siblings = stepsByParentGroupId.get(parentGroupId) ?? [];

    stepsByParentGroupId.set(parentGroupId, [...siblings, item]);
  });

  return items
    .filter(item => {
      if (item.contentType !== MessageContentType.ACTION_STEP) {
        return true;
      }

      return !isNonEmptyString(item.contentProps?.parentGroupId);
    })
    .map(item => {
      if (item.contentType !== MessageContentType.ACTION_STEP_GROUP) {
        return item;
      }

      const groupId = item.contentProps?.groupId;
      const nestedSteps = stepsByParentGroupId.get(groupId) ?? [];

      return {
        ...item,
        contentProps: {
          ...item.contentProps,
          nestedSteps,
        },
      };
    });
};

// Once an execution plan is attached to the message, the ActionPlan card is
// the canonical view of every tool execution and the streamed ACTION_STEP /
// ACTION_STEP_GROUP blocks become redundant duplicates. Suppress them so the
// UI shows one unified plan instead of two parallel rendering streams.
export const filterActionStepsCoveredByPlan = (items, planCoversToolExecutions) => {
  if (!planCoversToolExecutions || !Array.isArray(items)) {
    return items;
  }

  return items.filter(
    item =>
      (item.contentType !== MessageContentType.ACTION_STEP &&
        item.contentType !== MessageContentType.ACTION_STEP_GROUP) ||
      item.contentProps?.subStepType === ActionStepType.GUIDELINE_LOOKUP,
  );
};

export const isValidMessageContentItem = item => {
  if (_.isNil(item) || !_.isObject(item) || !item.contentType || !item.contentProps) {
    return false;
  }

  switch (item.contentType) {
    case MessageContentType.TEXT:
      return isTextContentProps(item.contentProps);
    case MessageContentType.CONFIRM_ACTION:
      return isConfirmActionContentProps(item.contentProps);
    case MessageContentType.LIST:
      return isListContentProps(item.contentProps);
    case MessageContentType.ACTION_STEP:
      return isActionStepContentProps(item.contentProps);
    case MessageContentType.ACTION_STEP_GROUP:
      return isActionStepGroupContentProps(item.contentProps);
    case MessageContentType.GUIDELINE_EXPANDER_CARD:
      return isGuidelineExpanderCardContentProps(item.contentProps);
    default:
      return false;
  }
};
